import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Package, FileText, Download, MessagesSquare, Loader2, ShieldCheck, Info } from "lucide-react";
import { PRODUCT_CONTENT, DOC_SLOT_LABELS } from "@/data/productContent";
import { SchedaAI } from "@/components/SchedaAI";
import { ProductPodcasts } from "@/components/ProductPodcasts";
import { useAuth } from "@/context/AuthContext";

export default function ProductHubB2B() {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consulenza, setConsulenza] = useState(null); // {code, name}
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/products/catalog")
      .then(({ data }) => setProducts(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const openDoc = (code, slot) => window.open(`${API}/products/${code}/documento/${slot}`, "_blank");

  const inviaConsulenza = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/products/${consulenza.code}/consulenza`, { note });
      toast.success(data.message || "Richiesta inviata.");
      setConsulenza(null);
      setNote("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="hub-prodotti">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-forest md:text-3xl">Hub Prodotti</h1>
        <p className="mt-1 text-muted-foreground">
          Schede tecniche e documentazione ufficiale dei prodotti. Riservato ai partner autorizzati.
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-forest/15 bg-[#F0F4F1] p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
        <span>Area B2B tecnica: nessuna vendita diretta. Per l'inquadramento di un rischio usa il preventivatore; per un supporto usa «Richiesta consulenza».</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>
      ) : (
        <div className="grid gap-5">
          {products.map((p) => {
            const content = PRODUCT_CONTENT[p.code];
            const tecnico = content?.hub?.tecnico || "";
            const docs = Object.entries(p.documenti || {}).filter(([, has]) => has);
            return (
              <Card key={p.code} data-testid={`hub-product-${p.code}`} className="rounded-xl border-border/60 p-6">
                <div className="flex items-start gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sage text-forest">
                    <Package className="h-6 w-6" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-heading text-xl font-semibold text-forest">{p.name}</h2>
                      {p.versione && <Badge variant="outline" className="border-forest/20 text-forest">Ed. {p.versione}</Badge>}
                    </div>
                    {tecnico && <p className="mt-2 text-sm text-muted-foreground">{tecnico}</p>}
                    {p.clusters?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {p.clusters.map((c) => <Badge key={c} variant="secondary" className="bg-sage text-forest">{c}</Badge>)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Documentazione ufficiale</span>
                  {docs.length === 0 ? (
                    <p className="mt-2 text-sm text-amber-600">Documentazione non ancora disponibile per questo prodotto.</p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {docs.map(([slot]) => (
                        <Button key={slot} size="sm" variant="outline" onClick={() => openDoc(p.code, slot)}
                          data-testid={`hub-doc-${p.code}-${slot}`} className="h-8 gap-1.5 text-xs text-forest">
                          <FileText className="h-3.5 w-3.5" /> {DOC_SLOT_LABELS[slot] || slot} <Download className="h-3 w-3 opacity-60" />
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                <SchedaAI code={p.code} name={p.name} isAdmin={isAdmin} />
                <ProductPodcasts code={p.code} name={p.name} />

                <div className="mt-5 flex flex-wrap gap-2 border-t border-border/50 pt-4">
                  <Button size="sm" onClick={() => { setConsulenza({ code: p.code, name: p.name }); setNote(""); }}
                    data-testid={`hub-consulenza-${p.code}`} className="gap-1.5 bg-forest hover:bg-forest/90">
                    <MessagesSquare className="h-4 w-4" /> Richiesta consulenza
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!consulenza} onOpenChange={(o) => !o && setConsulenza(null)}>
        <DialogContent data-testid="consulenza-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-forest">
              <ShieldCheck className="h-5 w-5" /> Richiesta consulenza — {consulenza?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Descrivi la tua richiesta all'Agenzia</Label>
            <Textarea data-testid="consulenza-note" value={note} onChange={(e) => setNote(e.target.value)} rows={4}
              placeholder="Es. inquadramento di un rischio specifico, chiarimenti su garanzie o documentazione…" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConsulenza(null)} data-testid="consulenza-annulla">Annulla</Button>
            <Button onClick={inviaConsulenza} disabled={sending} data-testid="consulenza-invia" className="gap-1.5 bg-forest hover:bg-forest/90">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia all'Agenzia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
